<template>
  <!-- eslint-disable vue/no-v-html -->
  <div
    :class="[
      $b({ 'body-xs': size === 'body-xs', matchPaddingToBanner, asBlock }),
      't-body',
      asBlock && 'global-wrapper-large'
    ]"
  >
    <div v-html="htmlOverride || preparedHtml" />

    <div
      v-if="cta && cta.active"
      :class="$b('cta-wrapper')"
    >
      <component
        :is="cta.gradient ? 'PrimaryButton' : 'TertiaryButton'"
        v-if="cta && cta.active"
        :to="cta.link"
      >
        {{ $tr(cta.copy) }}
      </component>
    </div>
  </div>
</template>

<script>
import { PrimaryButton, TertiaryButton } from '#components'

export default {
  name: 'RichText',

  components: {
    PrimaryButton,
    TertiaryButton,
  },

  props: {
    htmlOverride: {
      type: String,
      default: '',
    },
    content: {
      type: String,
      default: '',
    },
    translate: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: '',
      required: false,
    },
    matchPaddingToBanner: {
      type: Boolean,
      default: false,
    },
    cta: {
      type: Object,
      default: () => {},
    },
    asBlock: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    preparedHtml() {
      const contentWithPlaceholders = this.translate
        ? this.$tr(this.content)
        : this.content
      const contentAfterReplacements = this.replaceMarkdownVariables(
        contentWithPlaceholders
      )
      return this.$md.render(contentAfterReplacements)
    },
    markdownVariables() {
      const storefrontStore = useStorefrontStore()
      const shippingCountries = storefrontStore.shippingCountries
      const getShippingCost = (country) =>
        shippingCountries.find((c) => c.iso === country.toUpperCase())?.price

      return {
        'shipping:domestic': this.$tcl(getShippingCost('GB')),
        'shipping:eu': this.$tcl(getShippingCost('FR')),
        'shipping:row': this.$tcl(getShippingCost('US')),
      }
    },
  },

  created() {
    /** Open all links in a new tab */
    // Remember old renderer, if overridden, or proxy to default renderer
    const defaultRender =
      this.$md.renderer.rules.link_open ||
      function (tokens, idx, options, env, self) {
        return self.renderToken(tokens, idx, options)
      }

    this.$md.renderer.rules.link_open = function (
      tokens,
      idx,
      options,
      env,
      self
    ) {
      // If you are sure other plugins can't add `target` - drop check below
      const aIndex = tokens[idx].attrIndex('target')

      if (aIndex < 0) {
        tokens[idx].attrPush(['target', '_blank']) // add new attribute
      } else {
        tokens[idx].attrs[aIndex][1] = '_blank' // replace value of existing attr
      }

      // pass token to default renderer.
      return defaultRender(tokens, idx, options, env, self)
    }
  },

  methods: {
    replaceMarkdownVariables(string) {
      Object.entries(this.markdownVariables).forEach(([search, replace]) => {
        string = string.replace(`{{${search}}}`, replace)
      })
      return string
    },
  },
}
</script>

<style lang="postcss">
.RichText {
  &--match-padding-to-banner {
    --one-column: calc(1 / 12 * (100% - (11 * 32px)) + 32px);
    @screen sm {
      padding: 0 var(--one-column);
    }
  }

  > :not(:last-child) {
    @apply mb-sm;
  }

  /* These styles are replicated from type.css */
  h1,
  h2,
  h3,
  h4,
  h5 {
    @apply font-semibold;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  p {
    @apply font-sans
      antialiased
      normal-case;
  }

  h1,
  h2 {
    @apply text-md
      leading-tight
      tracking-tight
      mt-sm mb-sm
      md:text-2xl;
  }

  h3 {
    @apply text-base
      leading-tight
      tracking-tight
      md:text-lg
      mt-md mb-sm;
  }

  h4 {
    @apply text-sm
      leading-tight
      tracking-tight
      m-0
      md:text-base;
  }

  h5 {
    @apply text-xxs
      leading-tight
      tracking-wider
      m-0
      md:text-xs;
  }

  p {
    @apply text-sm md:text-base
      font-light
      leading-normal
      my-0 mb-sm;

    .is-default-standalone-theme & {
      @apply mdMax:text-[16px];
    }

    &:last-child {
      @apply mb-0;
    }

    strong {
      @apply font-bold;
    }
  }

  &--body-xs p {
    @apply text-xs;
  }

  blockquote {
    padding-left: theme('spacing.xs');
    border-left: 2px solid theme('colors.grey.DEFAULT');
  }

  a:not(.VisionButton) {
    @apply underline;
  }

  table {
    th,
    td {
      padding: theme('spacing.xxxs');
    }

    tr th {
      @apply text-left;
    }

    tr td {
      @apply text-xs
        align-top;
    }
  }

  ol {
    @apply mb-md ml-sm;
    list-style-type: decimal;
    list-style-position: inside;

    li {
      @apply text-sm md:text-base
        font-light
        leading-normal
        pb-xxs;

      &:last-of-type {
        @apply pb-0;
      }

      .is-default-standalone-theme & {
        @apply mdMax:text-[17px];
      }

      p {
        padding-left: 4px;
        display: inline;
      }
    }
  }

  ul {
    @apply mb-md ml-sm;
    list-style-type: disc;
    list-style-position: outside;

    li {
      @apply text-sm md:text-base
        font-light
        leading-normal
        pb-xxs;

      &:last-of-type {
        @apply pb-0;
      }

      .is-default-standalone-theme & {
        @apply mdMax:text-[17px];
      }
    }
  }
}
</style>
